import { WorkflowConfig } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { useEffect } from 'react';

import { getWorkflowInstrumentsForSdk } from './utils';
import {
  selectSelectedWorkcellId,
  selectWorkcellTransportConfigId,
} from '../../features/workflow/selectors';
import {
  setDriverActionConfigs,
  setSelectedWorkcellId,
  setWorkcellTransportConfig,
  setWorkcellTransportConfigType,
  setWorkcellTransportConfigVersion,
  setWorkflowInstruments,
} from '../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useGetWorkcell } from '../../hooks/useGetWorkcell';
import {
  DEFAULT_HARDCODED_WORKCELL_ID,
  SelectedWorkcellId,
  isSelectedWorkcellId,
} from '../../hooks/useGetWorkcell/types';
import { prepareInstrumentsForWorkflowConfig } from '../../utils';
import { useGetInstrumentsConfigState } from '../useGetInstrumentsConfigState';
import { useGetInstrumentsRenameMapping } from '../useGetInstrumentsRenameMapping';
import { useGetInstrumentsState } from '../useGetInstrumentsState';

export const useInitializeWorkcellAndDrivers = (
  workflowConfig: WorkflowConfig | undefined,
  isExistingWorkflow: boolean
) => {
  const dispatch = useAppDispatch();

  const selectedWorkcellId = useAppSelector(selectSelectedWorkcellId);
  const selectedTransportConfigId = useAppSelector(
    selectWorkcellTransportConfigId
  );

  // todo: once we do this, should we update the metadata type to canvas?
  const isSdkWorkflow = Boolean(
    workflowConfig?.metadata.workflow_type === 'sdk'
  );

  const workcellId: SelectedWorkcellId = isSelectedWorkcellId(
    selectedWorkcellId
  )
    ? selectedWorkcellId
    : DEFAULT_HARDCODED_WORKCELL_ID;

  const instrumentStates = useGetInstrumentsState(workcellId);
  const renameInstrumentsMapping = useGetInstrumentsRenameMapping(workcellId);
  const instrumentConfigStates = useGetInstrumentsConfigState(workcellId);

  const {
    data: getWorkcellData,
    isLoading: getWorkcellIsLoading,
    error: getWorkcellError,
  } = useGetWorkcell(workcellId, { enabled: !isSdkWorkflow });

  // workcell + driver initialization
  useEffect(() => {
    if (isSdkWorkflow && workflowConfig) {
      const workflowInstruments = getWorkflowInstrumentsForSdk(workflowConfig);
      dispatch(setSelectedWorkcellId('SDK_INTEROPERABILITY_WORKCELL_ID')); // workcell id set to empty string for sdk workflows
      dispatch(setWorkflowInstruments(workflowInstruments));
      dispatch(
        setWorkcellTransportConfig(
          workflowConfig.workcell.workcell_id ?? undefined
        )
      );
      dispatch(setWorkcellTransportConfigType(undefined));
      dispatch(setWorkcellTransportConfigVersion(undefined));
    } else {
      if (
        getWorkcellData?.driverActionConfigsByActionNameByVersionedDriverName
      ) {
        dispatch(
          setDriverActionConfigs(
            getWorkcellData.driverActionConfigsByActionNameByVersionedDriverName
          )
        );
      }
      const fetchedWorkcellInstruments =
        getWorkcellData?.workcellInstrumentsById;

      if (!isExistingWorkflow && fetchedWorkcellInstruments) {
        const preparedInstruments = prepareInstrumentsForWorkflowConfig(
          fetchedWorkcellInstruments,
          instrumentStates,
          renameInstrumentsMapping,
          instrumentConfigStates
        );

        dispatch(setWorkflowInstruments(preparedInstruments));
      }
      if (!isExistingWorkflow || selectedTransportConfigId === undefined) {
        dispatch(
          setWorkcellTransportConfig(getWorkcellData?.workcellTransportConfigId)
        );
        dispatch(setWorkcellTransportConfigType(undefined));
        dispatch(setWorkcellTransportConfigVersion(undefined));
      }
    }
  }, [
    dispatch,
    getWorkcellData?.driverActionConfigsByActionNameByVersionedDriverName,
    getWorkcellData?.workcellInstrumentsById,
    getWorkcellData?.workcellTransportConfigId,
    instrumentConfigStates,
    instrumentStates,
    isExistingWorkflow,
    isSdkWorkflow,
    renameInstrumentsMapping,
    selectedTransportConfigId,
    workflowConfig,
  ]);

  return {
    getWorkcellIsLoading,
    getWorkcellError,
    getWorkcellData,
  };
};
